import { useQueryClient } from '@tanstack/react-query';
import { motion, useReducedMotion } from 'framer-motion';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import { CloseIcon, CustomizeIcon, ExportIcon, SearchIcon } from '../../../../assets/icons/index';
import { AlertDialogWrapper } from '../../../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { CloseModalButton } from '../../../../components/close-modal-button/index';
import { Input } from '../../../../components/input/index';
import { Sheet, SheetContent } from '../../../../components/sheet/index';
import { QueryKey } from '../../../../constants/index';
import {
  ScenarioHooks,
  useDebounce,
  useInvalidateQueries,
  useModalState,
} from '../../../../hooks/index';
import { CapTableLoader } from '../../scenario-details/cap-table-loader';
import { useScenarioStore } from '../../scenario-modeling.store';
import { CustomizeModal } from './customize-modal';
import {
  CapTableTab,
  capTableTabTitleTitleMap,
  CustomView,
  defaultCustomView,
} from './fundraising.types';
import { Table } from './table-components/index';

export type ScenarioCapTableProps = {
  companyId: string;
  handleClose: () => void;
  isLoading: boolean;
  isOpen: boolean;
  scenarioId: string;
};

export const ScenarioFundraisingCapTable: FC<ScenarioCapTableProps> = ({
  companyId,
  handleClose: onCloseCallback,
  isOpen,
  scenarioId,
  isLoading,
}) => {
  const prefersReduced = useReducedMotion();
  const isFirstRender = useRef(true);
  const { mutateExport, isPending } = ScenarioHooks.useExport();
  const [search, setSearch] = useState<null | string>(null);
  const [isEmptySearchResult, setEmptySearchResult] = useState(false);
  const [selectedCapTableTab, setCapTableTab] = useState<CapTableTab>(CapTableTab.BY_STAKEHOLDER);
  const [customView, setCustomView] = useState<CustomView>({
    [CapTableTab.BY_SHARE_CLASS]: defaultCustomView[CapTableTab.BY_SHARE_CLASS],
    [CapTableTab.BY_STAKEHOLDER]: defaultCustomView[CapTableTab.BY_STAKEHOLDER],
  });

  const queryClient = useQueryClient();
  const { setSearch: setStoreSearch } = useScenarioStore();

  const { invalidateQuery } = useInvalidateQueries(
    QueryKey.CAP_TABLE_STAKEHOLDER_BY_ALL,
    QueryKey.CAP_TABLE_SHARE_CLASSES,
    QueryKey.CAP_TABLE_STAKEHOLDERS,
    QueryKey.CAP_TABLE_STAKEHOLDER_TYPES,
  );

  const [isOpenSearch, setOpenSearch] = useState(false);
  const inputRef = useRef<HTMLDivElement>(null);
  const [isFocusedInput, setInputFocus] = useState(false);

  const handleClose = () => {
    isFirstRender.current = true;
    onCloseCallback();
    setStoreSearch('');
    setSearch(null);
    setOpenSearch(false);
    setInputFocus(false);
  };

  const { debounceCallback } = useDebounce<string>({
    callback: (e) => {
      invalidateQuery();
      setStoreSearch(e);
    },
  });

  const handleSearch = useCallback(
    (value: string) => {
      setSearch(value);
      debounceCallback(value);
    },
    [debounceCallback],
  );

  const {
    isOpen: isOpenCustomizeModal,
    toggler: customizeModalToggler,
    handleOpenModal: handleOpenCustomizeModal,
    handleCloseModal: handleCloseCustomizeModal,
    handleSuccessModal: handleSuccessCustomizeModal,
  } = useModalState<CustomView>({
    onSuccess: (viewSettings) => {
      if (!viewSettings) return;
      toast.success(<AppFormattedMessage id="CHANGES_SAVED" />);
      setCustomView(viewSettings);
    },
  });

  useEffect(() => {
    if (!search) {
      setEmptySearchResult(false);
      queryClient.refetchQueries({ queryKey: [QueryKey.CAP_TABLE_STAKEHOLDER_BY_ALL] });
      queryClient.refetchQueries({ queryKey: [QueryKey.CAP_TABLE_SHARE_CLASSES] });
      queryClient.refetchQueries({ queryKey: [QueryKey.CAP_TABLE_STAKEHOLDERS] });
      queryClient.refetchQueries({ queryKey: [QueryKey.CAP_TABLE_STAKEHOLDER_TYPES] });
    }
  }, [queryClient, search]);

  return (
    <>
      <AlertDialogWrapper
        control={{ open: isOpenCustomizeModal, onOpenChange: customizeModalToggler }}
      >
        <CustomizeModal
          onCancel={handleCloseCustomizeModal}
          onSuccess={handleSuccessCustomizeModal}
          selectedCapTableTab={selectedCapTableTab}
          viewSettings={customView}
        />
      </AlertDialogWrapper>

      <Sheet onOpenChange={(open) => !open && handleClose()} open={isOpen}>
        <SheetContent
          className={twMerge(
            'border-transparent bg-transparent p-3 shadow-none transition-all',
            isLoading ? 'h-fit w-[453px]' : 'h-fit max-h-[90wh] w-[96%] max-w-[96%]',
          )}
          side="CENTER"
        >
          {isLoading ? (
            <CapTableLoader isLoading={isLoading} placement="CUSTOM" />
          ) : (
            <div className="flex h-fit w-full flex-col rounded-2xl bg-white p-4">
              <div className="flex w-full items-center justify-between gap-2 px-4 pb-[18px] pt-[2px]">
                <span className="text-base font-semibold text-gray-700">
                  <AppFormattedMessage id="SCENARIO_CAP_TABLE" />
                </span>
                <CloseModalButton onClose={handleClose} />
              </div>
              <div className="flex w-full flex-col rounded-md shadow-sm">
                <motion.div
                  animate={prefersReduced ? { opacity: 1 } : { opacity: 1, y: 0 }}
                  className="flex items-center justify-between gap-4 p-4"
                  initial={!isFirstRender.current || prefersReduced ? false : { opacity: 0, y: 6 }}
                  onAnimationComplete={() => {
                    isFirstRender.current = false;
                  }}
                  transition={{ delay: 0.5, duration: 0.28, ease: 'easeOut' }}
                >
                  <div className="flex h-9 divide-x divide-gray-200 rounded border border-gray-200">
                    {capTableTabTitleTitleMap.map(([filterKey, filter]) => (
                      <Button
                        className={twMerge(
                          'h-full w-fit rounded-none px-4 text-sm font-[450] text-[#858593]',
                          selectedCapTableTab === filterKey &&
                            'bg-brand-25 text-sm font-[550] text-[#172335]',
                        )}
                        key={filterKey}
                        onClick={() => setCapTableTab(filterKey)}
                        styleType="NONE"
                      >
                        <AppFormattedMessage id={filter} />
                      </Button>
                    ))}
                  </div>
                  <div className="relative flex gap-3">
                    <div className="flex items-center justify-end" ref={inputRef}>
                      <Input
                        inputClassName="h-9"
                        onBlur={() => setInputFocus(false)}
                        onChange={handleSearch}
                        onFocus={() => {
                          setInputFocus(true);
                        }}
                        placeholder={
                          search || isFocusedInput ? '' : <AppFormattedMessage id="SEARCH" />
                        }
                        value={search || ''}
                        wrapperClassName={twMerge(
                          'transition-all duration-500 w-[200px] overflow-hidden h-9',
                          isOpenSearch ? 'delay-500' : 'w-0',
                        )}
                      />
                      {isOpenSearch && typeof search === 'string' && (
                        <div
                          className="absolute mr-10 cursor-pointer"
                          onClick={() => {
                            setSearch('');
                            setStoreSearch('');
                            invalidateQuery();
                          }}
                        >
                          <CloseIcon />
                        </div>
                      )}
                      <Button
                        className={twMerge(
                          'size-9 border-[1px] border-gray-100 bg-gray-50 transition-all duration-300',
                          isOpenSearch
                            ? 'h-9 rounded-l-none rounded-br-none border-x-0 border-t-0 border-b-black'
                            : 'delay-500',
                          isFocusedInput && 'border-b-[2px] border-b-brand-700 transition-none',
                        )}
                        onClick={() => {
                          setOpenSearch((prev) => !prev);
                          setSearch(null);
                          setStoreSearch('');
                          invalidateQuery();
                        }}
                      >
                        <SearchIcon iconColor={isFocusedInput ? '#2565C8' : '#344054'} />
                      </Button>
                    </div>
                    <Button
                      className="flex size-9 gap-1 border-[1px] border-gray-100 bg-gray-25 px-3 py-[6px] lg:w-fit"
                      isLoading={isPending}
                      onClick={() => mutateExport({ body: customView })}
                    >
                      <ExportIcon className="rotate-180" />
                      <span className="text-sm font-[450] text-gray-700 max-lg:hidden">
                        <AppFormattedMessage id="EXPORT" />
                      </span>
                    </Button>

                    <Button
                      className="flex size-9 gap-1 border-[1px] border-gray-100 bg-gray-25 px-3 py-[6px] lg:w-fit"
                      onClick={handleOpenCustomizeModal}
                    >
                      <CustomizeIcon />
                      <span className="text-sm font-[450] text-gray-700 max-lg:hidden">
                        <AppFormattedMessage id="CUSTOMIZE_VIEW" />
                      </span>
                    </Button>
                  </div>
                </motion.div>
                <div className="h-full max-h-[calc(90vh-160px)] overflow-auto">
                  <Table
                    companyId={companyId}
                    isEmptySearchResult={isEmptySearchResult}
                    scenarioId={scenarioId}
                    tab={selectedCapTableTab}
                    viewBy={customView[selectedCapTableTab]?.viewBy}
                    visibleColumns={customView[selectedCapTableTab].columns}
                  />
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
};
